import React, { useState, useEffect, useMemo } from 'react';
import {
  BookOpen,
  ArrowLeft,
  ArrowRight,
  Download,
  Lock,
  Loader2,
} from 'lucide-react';
import { PraxisPaper, StudyGuideRecord, UserProfile } from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { ExamCatalogOverview } from './ExamCatalogOverview';
import {
  getPaperCatalogSummaries,
  getStudyGuidesFromFirestore,
  getStudyGuidePdfBlobUrl,
  isPaperUnlockedForUser,
} from '../lib/firebase';

interface StudyGuidesProps {
  // Kept optional/nullable so a plain click on the top-nav "Study Guides"
  // tab (no specific paper in mind) always lands on the family picker,
  // while arriving via a paper's "Guide" button (App.tsx's
  // handleOpenGuides) can still deep-link straight to that paper's PDF.
  initialPaperId?: string | null;
  onStartTest: (paperId: string) => void;
  userProfile?: UserProfile;
  openPricingModal?: () => void;
  // Web guides (2026-10-28): papers listed here open the web reader.
  webGuideIds?: Set<string>;
  onOpenWebGuide?: (paperId: string) => void;
}

// Same family/paper matching heuristic ExamDetailPage.tsx uses to decide
// which PraxisPapers belong under an Exam Catalog entry -- duplicated here
// (rather than imported) so a study guide's parent paper resolves to the
// exact same exam family a student would see when practicing that paper.
function paperMatchesExamCode(paper: PraxisPaper, targetExamCodeRaw: string): boolean {
  const paperExamCode = paper.examCode ? paper.examCode.trim() : '';
  const targetExamCode = targetExamCodeRaw.trim();

  if (paperExamCode && paperExamCode.toLowerCase() === targetExamCode.toLowerCase()) {
    return true;
  }
  if (paperExamCode) {
    const lowerP = paperExamCode.toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerP.startsWith(lowerTarget + '-') || lowerP === lowerTarget) {
      return true;
    }
  }
  if (paper.code) {
    const lowerCode = paper.code.trim().toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerCode.startsWith(lowerTarget + '-') || lowerCode === lowerTarget) {
      return true;
    }
  }
  if (targetExamCode === 'Praxis-Core' && paper.category === 'core' && (!paperExamCode || paperExamCode.startsWith('Praxis'))) return true;
  if (targetExamCode === 'Praxis-Elementary' && (paper.category === 'elementary' || paper.code === '5001')) return true;
  if (targetExamCode === 'Praxis-Subject' && (paper.category === 'subject' || paper.category === 'elementary')) return true;
  if (targetExamCode === 'Praxis-PLT' && paper.category === 'plt') return true;

  return false;
}

function getPapersForFamily(entry: ExamCatalogEntry, papers: PraxisPaper[]): PraxisPaper[] {
  const matched: PraxisPaper[] = [];
  const seen = new Set<string>();
  entry.examCodes.forEach((code) => {
    papers.forEach((p) => {
      if (!seen.has(p.id) && paperMatchesExamCode(p, code)) {
        seen.add(p.id);
        matched.push(p);
      }
    });
  });
  return matched;
}

function findFamilyForPaper(paper: PraxisPaper): ExamCatalogEntry | null {
  return (
    examCatalog.find((entry) =>
      entry.examCodes.some((code) => paperMatchesExamCode(paper, code)),
    ) || null
  );
}

type ViewStep = 'family' | 'tiles' | 'viewer';

export const StudyGuides: React.FC<StudyGuidesProps> = ({
  initialPaperId,
  onStartTest,
  userProfile,
  openPricingModal,
  webGuideIds,
  onOpenWebGuide,
}) => {
  const [papers, setPapers] = useState<PraxisPaper[]>(PRAXIS_PAPERS);
  const [allGuides, setAllGuides] = useState<StudyGuideRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [viewStep, setViewStep] = useState<ViewStep>('family');
  const [selectedFamily, setSelectedFamily] = useState<ExamCatalogEntry | null>(null);
  const [selectedGuide, setSelectedGuide] = useState<StudyGuideRecord | null>(null);
  const [deepLinkHandled, setDeepLinkHandled] = useState<boolean>(false);
  const [guidesLoading, setGuidesLoading] = useState<boolean>(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);

  // Load every paper once. The catalog is small enough (dozens of papers)
  // that filtering by family client-side is simplest, and it mirrors how
  // PaperTabs/ExamDetailPage already work.
  useEffect(() => {
    async function loadPapers() {
      setLoading(true);
      try {
        const fp = await getPaperCatalogSummaries();
        if (fp && fp.length > 0) setPapers(fp);
      } catch (err) {
        console.warn('StudyGuides: error loading papers:', err);
      } finally {
        setLoading(false);
      }
    }
    loadPapers();
  }, []);

  const familyPapers = useMemo(
    () => (selectedFamily ? getPapersForFamily(selectedFamily, papers) : []),
    [selectedFamily, papers],
  );

  // Study guides are only ever fetched once a family (and therefore a
  // concrete set of paperIds) is known -- never as one unconstrained
  // "fetch every guide" call. The live studyGuides Firestore rule is
  // gated on resource.data.paperId (allow read: if isAdmin() ||
  // hasPaperAccess(resource.data.paperId)) and only tolerates queries that
  // filter on that field per paper -- an unconstrained list call is
  // rejected outright for non-admins, which is what left this page stuck
  // on "Loading study guides..." forever. Fetching one scoped query per
  // paper in the family (rather than loosening the rule) keeps the
  // subscription paywall on study guide content intact.
  useEffect(() => {
    if (!selectedFamily || familyPapers.length === 0) {
      setAllGuides([]);
      return;
    }
    let cancelled = false;
    async function loadFamilyGuides() {
      setGuidesLoading(true);
      try {
        const results = await Promise.all(
          familyPapers.map((p) => getStudyGuidesFromFirestore(p.id)),
        );
        if (!cancelled) setAllGuides(results.flat());
      } catch (err) {
        console.warn('StudyGuides: error loading guides for family:', err);
        if (!cancelled) setAllGuides([]);
      } finally {
        if (!cancelled) setGuidesLoading(false);
      }
    }
    loadFamilyGuides();
    return () => {
      cancelled = true;
    };
  }, [selectedFamily, familyPapers]);

  // Deep link: arriving here because a student clicked "Guide" on a specific
  // paper (App.tsx's handleOpenGuides) should jump straight to that paper's
  // guide when one exists and is unlocked, or at least land on its exam
  // family's tile grid instead of the generic family picker.
  // Step 1: once papers are loaded, resolve the target paper's family and
  // select it -- this is what triggers the family-scoped guide fetch above.
  useEffect(() => {
    if (loading || deepLinkHandled || !initialPaperId) return;

    const targetPaper = papers.find(
      (p) => p.id === initialPaperId || p.code === initialPaperId,
    );
    if (!targetPaper) {
      setDeepLinkHandled(true);
      return;
    }

    const family = findFamilyForPaper(targetPaper);
    if (family) {
      setSelectedFamily(family);
    } else {
      setDeepLinkHandled(true);
    }
  }, [loading, deepLinkHandled, initialPaperId, papers]);

  // Step 2: once that family's guides have actually loaded, decide whether
  // to jump straight to the PDF viewer or land on the tile grid.
  useEffect(() => {
    if (deepLinkHandled || !initialPaperId || !selectedFamily || guidesLoading) return;

    const targetPaper = papers.find(
      (p) => p.id === initialPaperId || p.code === initialPaperId,
    );
    if (!targetPaper) {
      setDeepLinkHandled(true);
      return;
    }

    const isUnlocked =
      !userProfile ||
      isPaperUnlockedForUser(userProfile, targetPaper.code) ||
      isPaperUnlockedForUser(userProfile, targetPaper.id);
    const guide = allGuides.find((g) => g.paperId === targetPaper.id);

    if (guide && isUnlocked) {
      setSelectedGuide(guide);
      setViewStep('viewer');
    } else {
      setViewStep('tiles');
    }
    setDeepLinkHandled(true);
  }, [deepLinkHandled, initialPaperId, selectedFamily, guidesLoading, allGuides, papers, userProfile]);

  // The PDF is never read from selectedGuide.pdfUrl directly -- that field
  // (when present at all) is a legacy permanent public Storage download-token
  // link that bypasses the subscription paywall once issued. Every guide is
  // now fetched through the authenticated /api/study-guides/:id/pdf endpoint,
  // which re-checks the caller's subscription/unlock status on every request,
  // and turned into a short-lived local blob: URL for the iframe/download link.
  useEffect(() => {
    if (viewStep !== 'viewer' || !selectedGuide) {
      return;
    }
    if (!selectedGuide.pdfUrl && !selectedGuide.pdfStoragePath) {
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    async function loadGuidePdf() {
      setPdfLoading(true);
      setPdfLoadError(null);
      setPdfBlobUrl(null);
      try {
        const blobUrl = await getStudyGuidePdfBlobUrl(selectedGuide!.id);
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        objectUrl = blobUrl;
        setPdfBlobUrl(blobUrl);
      } catch (err: any) {
        if (!cancelled) {
          setPdfLoadError(err?.message || 'Unable to load this study guide PDF.');
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    }
    loadGuidePdf();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [viewStep, selectedGuide]);

  const familyGuides = useMemo(() => {
    if (!selectedFamily) return [];
    const paperIds = new Set(familyPapers.map((p) => p.id));
    return allGuides.filter((g) => paperIds.has(g.paperId));
  }, [selectedFamily, familyPapers, allGuides]);

  const handleSelectFamily = (entry: ExamCatalogEntry) => {
    setSelectedFamily(entry);
    setViewStep('tiles');
  };

  const handleBackToFamilies = () => {
    setSelectedFamily(null);
    setSelectedGuide(null);
    setViewStep('family');
  };

  const handleBackToTiles = () => {
    setSelectedGuide(null);
    setViewStep('tiles');
  };

  const handleOpenGuideTile = (guide: StudyGuideRecord, paper?: PraxisPaper) => {
    const isUnlocked =
      !userProfile ||
      !paper ||
      isPaperUnlockedForUser(userProfile, paper.code) ||
      isPaperUnlockedForUser(userProfile, paper.id);
    if (!isUnlocked) {
      if (openPricingModal) openPricingModal();
      return;
    }
    setSelectedGuide(guide);
    setViewStep('viewer');
  };

  if (loading) {
    return (
      <div className="py-24 flex flex-col items-center justify-center gap-3 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
        <p className="text-sm font-medium">Loading study guides...</p>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // STEP 3: PDF VIEWER
  // ---------------------------------------------------------------------
  if (viewStep === 'viewer' && selectedGuide) {
    const paper = papers.find((p) => p.id === selectedGuide.paperId);
    return (
      <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <button
            onClick={handleBackToTiles}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Study Guides
          </button>

          {(selectedGuide.pdfUrl || selectedGuide.pdfStoragePath) && pdfBlobUrl && (
            <a
              href={pdfBlobUrl}
              download={selectedGuide.pdfName || `${selectedGuide.title}.pdf`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs"
            >
              <Download className="w-3.5 h-3.5" /> Download PDF
            </a>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-1">
          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-200 inline-block">
            {paper
              ? `PRAXIS ${paper.code}`
              : selectedGuide.paperCode
                ? `PRAXIS ${selectedGuide.paperCode}`
                : 'Study Guide'}
          </span>
          <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">
            {selectedGuide.title}
          </h1>
          {selectedGuide.summary && (
            <p className="text-sm text-slate-500">{selectedGuide.summary}</p>
          )}
        </div>

        {selectedGuide.pdfUrl || selectedGuide.pdfStoragePath ? (
          pdfLoading ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xs py-24 flex flex-col items-center justify-center gap-3 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
              <p className="text-sm font-medium">Loading PDF...</p>
            </div>
          ) : pdfLoadError ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-8 text-sm text-red-600">
              {pdfLoadError}
            </div>
          ) : pdfBlobUrl ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
              <iframe
                src={pdfBlobUrl}
                title={selectedGuide.title}
                className="w-full h-[75vh]"
              />
            </div>
          ) : null
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-8 text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
            {selectedGuide.contentMarkdown}
          </div>
        )}

        {paper && (
          <div className="flex justify-center pt-2">
            <button
              onClick={() => onStartTest(paper.id)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-xl transition shadow-md"
            >
              Test Knowledge on PRAXIS {paper.code} Exam &rarr;
            </button>
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // STEP 2: TILE / DECK GRID FOR THE SELECTED EXAM FAMILY
  // ---------------------------------------------------------------------
  if (viewStep === 'tiles' && selectedFamily) {
    return (
      <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-6">
        <div>
          <button
            onClick={handleBackToFamilies}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Exam Directory
          </button>
          <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-wider">
            <BookOpen className="w-4 h-4" /> Study Guides
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
            {selectedFamily.state} Study Guides
          </h1>
          <p className="text-sm text-slate-500 mt-1">{selectedFamily.examLabel}</p>
        </div>

        {guidesLoading ? (
          <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <p className="text-sm font-medium">Loading study guides...</p>
          </div>
        ) : familyPapers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-10 text-center text-sm text-slate-500">
            No exam papers are configured for this exam family yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {familyPapers.map((paper) => {
              const guide = familyGuides.find((g) => g.paperId === paper.id);
              const hasWeb = !!webGuideIds && webGuideIds.has(paper.id) && !!onOpenWebGuide;
              const isUnlocked =
                !userProfile ||
                isPaperUnlockedForUser(userProfile, paper.code) ||
                isPaperUnlockedForUser(userProfile, paper.id);
              const clickable = hasWeb || !!guide;

              return (
                <div
                  key={paper.id}
                  onClick={() => {
                    if (hasWeb) onOpenWebGuide!(paper.id);
                    else if (guide) handleOpenGuideTile(guide, paper);
                  }}
                  className={`group rounded-2xl border p-5 space-y-3 transition-all bg-gradient-to-br from-indigo-50 via-white to-white ${
                    clickable
                      ? 'cursor-pointer border-slate-200 shadow-xs hover:shadow-lg hover:border-indigo-400'
                      : 'border-slate-100 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="w-11 h-11 rounded-full bg-indigo-600 text-white font-black text-[11px] flex items-center justify-center shrink-0 px-1 text-center leading-none">
                      {paper.code}
                    </span>
                    {hasWeb ? (
                      <span className="text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 uppercase">
                        {isUnlocked ? 'Web guide' : 'Chapter 1 free'}
                      </span>
                    ) : !isUnlocked ? (
                      <Lock className="w-4 h-4 text-amber-500" />
                    ) : !clickable ? (
                      <span className="text-[10px] font-bold text-slate-400 uppercase">
                        Coming Soon
                      </span>
                    ) : null}
                  </div>

                  <div>
                    <div className="text-[10px] font-extrabold text-indigo-500 uppercase tracking-wider">
                      {paper.examCode && !paper.examCode.toLowerCase().startsWith('praxis')
                        ? paper.examCode
                        : 'PRAXIS'}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 leading-snug line-clamp-2">
                      {guide ? guide.title : paper.title}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {guide?.summary || paper.description}
                    </p>
                  </div>

                  {clickable && (
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-indigo-600">
                      <span>{hasWeb ? 'Read study guide' : 'View study guide'}</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // STEP 1 (DEFAULT): EXAM FAMILY PICKER
  // Reuses the existing Exam Catalog overview grid so Study Guides and
  // practice exams present the exact same family list (per Peter's choice
  // to reuse the Exam Catalog families rather than a separate list).
  // ---------------------------------------------------------------------
  return (
    <div className="space-y-2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-wider">
          <BookOpen className="w-4 h-4" /> Study Guides
        </div>
      </div>
      <ExamCatalogOverview onSelectExam={handleSelectFamily} />
    </div>
  );
};
